import React, { useState } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { PageLayout } from '../components/PageLayout';

export function Account() {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletionSuccess, setDeletionSuccess] = useState(false);

  async function handleDeleteAccount() {
    setIsDeleting(true);
    setError(null);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const response = await fetch('/api/account', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete account. Please try again.');
      }

      setDeletionSuccess(true);
      setShowConfirmation(false);

      // Sign out after brief delay to allow user to see the success message
      setTimeout(async () => {
        await signOut();
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setShowConfirmation(false);
    } finally {
      setIsDeleting(false);
    }
  }

  if (deletionSuccess) {
    return (
      <PageLayout>
        <main className="account-page" role="main" aria-labelledby="deletion-success-heading">
          <div className="deletion-success" role="status" aria-live="polite">
            <h2 id="deletion-success-heading">Account Deleted</h2>
            <p>
              Your account and all associated data have been successfully deleted.
              You will be redirected to the sign-in page shortly.
            </p>
          </div>
        </main>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <main className="account-page" role="main" aria-labelledby="account-heading">
        <h2 id="account-heading">Account</h2>

        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        <section className="account-section" aria-labelledby="delete-heading">
          <h3 id="delete-heading">Delete Account</h3>
          <p className="setting-description">
            Permanently delete your account and all associated data, including
            retained audit reports and authentication records. This action cannot
            be undone.
          </p>

          {!showConfirmation ? (
            <button
              onClick={() => setShowConfirmation(true)}
              className="delete-button"
              aria-label="Delete account"
            >
              Delete My Account
            </button>
          ) : (
            <div
              className="confirmation-dialog"
              role="alertdialog"
              aria-labelledby="confirm-heading"
              aria-describedby="confirm-description"
            >
              <h4 id="confirm-heading">Are you sure?</h4>
              <p id="confirm-description">
                This will permanently delete your account and all associated data.
                This cannot be undone.
              </p>
              <div className="confirmation-actions">
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="confirm-delete-button"
                  aria-label="Confirm account deletion"
                >
                  {isDeleting ? 'Deleting...' : 'Yes, Delete My Account'}
                </button>
                <button
                  onClick={() => setShowConfirmation(false)}
                  disabled={isDeleting}
                  className="cancel-button"
                  aria-label="Cancel account deletion"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </PageLayout>
  );
}
